import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import './styles/darkModeEnhanced.css'
import './styles/lightModeReadability.css'
import './styles/dashboardTextContrast.css'
import './styles/dashboardMobile.css'
import App from './App.jsx'
import ErrorBoundary from './components/ErrorBoundary'
import { initializeTelemetry } from './config/telemetryLoader'

// Suppress browser extension errors (React DevTools, etc.) in development
if (import.meta.env.DEV) {
  const originalError = console.error;
  console.error = (...args) => {
    const errorMessage = args[0]?.toString() || '';
    // Filter out extension-related errors
    if (
      errorMessage.includes('disconnected port object') ||
      errorMessage.includes('Extension context invalidated')
    ) {
      return; // Suppress these specific errors
    }
    originalError.apply(console, args);
  };
}

// Stale-deploy recovery: Vite fires this when preloading a hashed chunk fails
// (old index.html in a long-lived tab after a deploy renamed the chunks).
// Reload once to pick up the new build; ErrorBoundary handles the same for
// dynamic imports that bypass preload (with its own 30s loop guard).
window.addEventListener('vite:preloadError', (event) => {
  const key = 'evv_chunk_reload_at';
  const last = parseInt(sessionStorage.getItem(key) || '0', 10);
  if (Date.now() - last > 30000) {
    event.preventDefault(); // suppress the throw — we're handling it
    sessionStorage.setItem(key, String(Date.now()));
    window.location.reload();
  }
});

// Start monitoring downloads without blocking the application bundle or first
// render. The bridge loads only vendors configured for this environment.
void initializeTelemetry()

// Root error boundary. Without one, ANY render error unmounts the whole tree
// and the user gets a blank page with no message and no way to recover — which
// is what a clinician reported on 2026-08-18 after rebooting and retrying for
// an hour. ErrorBoundary also auto-reloads once on a stale-chunk error, so a
// post-deploy tab heals itself instead of going white.
createRoot(document.getElementById('root')).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>,
)
